import { CalendarIcon, ClockIcon, GiftIcon } from "@xola/icons";
import clsx from "clsx";
import { DateFormat, isBaseGiftProduct } from "common";
import dayjs from "dayjs";
import { isNil } from "lodash";
import React from "react";
import { Experience, GiftOffer, Package } from "types";
import { useResource } from "../../hooks/useResource";
import { CreatePurchaseItemState, CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { Loader } from "../Loader";

interface PurchaseItemSummaryPreviewProps {
    item: CreatePurchaseItemState;
    isActive?: boolean;
}

export const PurchaseItemSummaryPreview = ({ item, isActive = false }: PurchaseItemSummaryPreviewProps) => {
    const { data: product, isLoading } = useResource<Experience | GiftOffer | Package>(["products", item.product.id]);

    const step = useCreatePurchaseStore((state) => state.step);
    const isVariableDemographicsPackage = useCreatePurchaseStore((state) => state.isVariableDemographicsPackage());

    if (isLoading || !product) {
        return <Loader />;
    }

    const hasArrivalDateTime = item.arrivalDate;

    const hasDuration = "eventDuration" in product || "duration" in product;

    const itemArrivalDateTime = item.arrivalTime
        ? dayjs(item.arrivalDate)
              .hour(Math.floor(item.arrivalTime / 100))
              .minute(item.arrivalTime % 100)
        : dayjs(item.arrivalDate);
    const formattedArrivalDateTime = itemArrivalDateTime
        ? itemArrivalDateTime.format(DateFormat.DAY_WITH_FULL_MONTH_NAME_DATE)
        : null;

    const eventDuration = hasDuration ? product?.eventDuration ?? product?.duration : 0;

    const startDate = itemArrivalDateTime ? itemArrivalDateTime.format(DateFormat.DAY_WITH_FULL_MONTH_NAME_DATE) : null;
    const endDate = itemArrivalDateTime
        ? itemArrivalDateTime.add(eventDuration, "minutes").format(DateFormat.DAY_WITH_FULL_MONTH_NAME_DATE)
        : null;
    const formattedRangeDate = `${startDate} - ${endDate}`;

    const hasArrivalTime = hasArrivalDateTime && !isNil(item.arrivalTime);

    const isCheckout = step === CreatePurchaseStep.CHECKOUT;

    return (
        <div
            className={clsx(
                "space-y-3 rounded py-5 px-6",
                getPurchaseItemClasses(isActive, isVariableDemographicsPackage, isCheckout),
            )}
            data-testid="purchase-item-summary-product"
        >
            <div className="flex justify-between space-x-1">
                <div className="flex items-center">
                    {isBaseGiftProduct(product) ? (
                        <div className="mr-3 flex-shrink-0 rounded-lg border border-gray bg-white p-2">
                            <GiftIcon size="medium" />
                        </div>
                    ) : (
                        <ProductImage product={product} size="small" className="!mr-1" />
                    )}

                    <div className="w-full">
                        <div className="text-md font-bold">{product.name}</div>
                        <div className="flex w-full space-x-2 text-gray-darker">
                            <span>
                                <CalendarIcon />{" "}
                                <NoTranslate data-testid="purchase-item-summary-date">
                                    {eventDuration >= 1440 ? formattedRangeDate : formattedArrivalDateTime}
                                </NoTranslate>
                            </span>
                            {hasArrivalTime ? (
                                <NoTranslate data-testid="purchase-item-summary-time" className="whitespace-nowrap">
                                    <ClockIcon /> {itemArrivalDateTime?.format(DateFormat.TIME)}
                                </NoTranslate>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const getPurchaseItemClasses = (
    isActive: boolean,
    isVariableDemographicsPackage: boolean,
    isCheckout: boolean,
) => {
    let activeItem = "border border-primary bg-primary-lighter";
    let inactiveItem = "bg-white";

    if (isVariableDemographicsPackage) {
        inactiveItem = "border border-white bg-white hover:border hover:border-gray hover:bg-gray-hover";
        activeItem = isCheckout ? inactiveItem : activeItem;
    }

    return isActive ? activeItem : inactiveItem;
};
